/**
 * @module public/js/pages/homeGamificationRank
 * @summary Renders the tenant-gated weekly rank message on the home screen.
 */

import { formatGamificationDateKey } from './gamificationDashboardUtils.js';

/**
 * Resolve the browser timezone for the current-week summary request.
 *
 * @returns {string} IANA timezone id or UTC.
 */
export function resolveHomeRankTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Build display copy from a gamification personal summary.
 *
 * @param {Record<string, unknown>} personal - Personal summary payload.
 * @returns {{headline: string, detail: string}|null} Prompt copy or null when rank is unavailable.
 */
export function buildHomeRankPromptModel(personal = {}) {
  const position = Number(personal?.rank?.position);
  const total = Number(personal?.rank?.total);
  if (
    !Number.isInteger(position) ||
    !Number.isInteger(total) ||
    position < 1 ||
    total < 1 ||
    position > total
  ) {
    return null;
  }

  const tied = personal?.rank?.tied === true;
  return {
    headline: `${tied ? 'Tied at' : 'You’re'} #${position} of ${total} this week`,
    detail: 'See your momentum score, streaks, and badges.',
  };
}

/**
 * Fetch and mount the clickable home-screen rank prompt.
 *
 * @param {object} params - Mount options.
 * @param {HTMLElement} params.container - Home button stack.
 * @param {{fetchJson: Function}} params.apiClient - Authenticated API client.
 * @param {{track?: Function}|null} [params.analytics=null] - Optional analytics service.
 * @param {(path: string) => void} [params.navigate] - Navigation adapter.
 * @param {Date} [params.now=new Date()] - Request date.
 * @returns {Promise<HTMLButtonElement|null>} Mounted prompt button or null.
 */
export async function mountHomeGamificationRank({
  container,
  apiClient,
  analytics = null,
  navigate = (path) => {
    window.location.href = path;
  },
  now = new Date(),
}) {
  if (!container || typeof apiClient?.fetchJson !== 'function') return null;

  const timeZone = resolveHomeRankTimeZone();
  let response;
  try {
    response = await apiClient.fetchJson('/runGamificationReport', {
      auth: true,
      rawResponse: true,
      body: {
        endDate: formatGamificationDateKey(now, timeZone),
        summaryOnly: true,
        timeZone,
      },
    });
  } catch (error) {
    const message = String(error?.message || error || '');
    if (/Failed to fetch|NetworkError|Load failed/i.test(message)) {
      console.debug('[homeGamificationRank] rank summary unavailable', {
        message,
      });
      return null;
    }
    throw error;
  }
  if (typeof response?.ok === 'boolean' && !response.ok) return null;
  const data =
    typeof response?.json === 'function' ? await response.json() : response;
  if (data?.partial === true) return null;
  const model = buildHomeRankPromptModel(data?.personal);
  if (!model) return null;

  const button = document.createElement('button');
  button.id = 'homeGamificationRank';
  button.className = 'home-rank-prompt';
  button.type = 'button';
  button.setAttribute('aria-label', `${model.headline}. ${model.detail}`);

  const icon = document.createElement('span');
  icon.className = 'home-rank-prompt__icon';
  icon.setAttribute('aria-hidden', 'true');
  icon.textContent = '↗';

  const copy = document.createElement('span');
  copy.className = 'home-rank-prompt__copy';
  const headline = document.createElement('strong');
  headline.textContent = model.headline;
  const detail = document.createElement('span');
  detail.textContent = model.detail;
  copy.append(headline, detail);

  const action = document.createElement('span');
  action.className = 'home-rank-prompt__action';
  action.textContent = 'View momentum';
  button.append(icon, copy, action);
  button.addEventListener('click', () => {
    analytics?.track?.('Landing - Gamification');
    navigate('/gamification.html');
  });

  container.prepend(button);
  return button;
}
